import { createFileRoute } from '@tanstack/react-router'

import {useState} from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import {Button} from '../../components/button';
import {Input} from '../../components/input';
import {Field, Label} from '../../components/fieldset';
import {apiFetch} from '../../utils/api';
import {resetAuthCache} from '../../components/AuthWrapper';
import {reinitializeTranslations} from '../../hooks/useI18n';

const ButtonAny = Button as any;
const InputAny = Input as any;

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiFetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      if (response.ok) {
        // Store tokens in localStorage
        const data = await response.json();
        if (data.access_token && data.refresh_token) {
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
        }

        // Reset auth cache to recognize the new authentication state
        resetAuthCache();

        // Reinitialize translations to load user's language preference
        await reinitializeTranslations();

        // Redirect to main app
        navigate({ to: '/'});
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await apiFetch('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          organization_id: '00000000-0000-0000-0000-000000000001', // Default Daisuke org
          email: registerData.email,
          password: registerData.password,
          full_name: registerData.email.split('@')[0], // Use email prefix as name
          is_superuser: false,
        }),
      });

      if (response.ok) {
        setShowRegister(false);
        setError('');
        alert('Registration successful! Please log in.');
        setRegisterData({email: '', password: '', confirmPassword: ''});
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {showRegister ? 'Create your account' : 'Sign in to your account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Cafe Magic
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {showRegister ? (
          <form className="mt-8 space-y-6" onSubmit={handleRegister}>
            <div className="space-y-4">
              <Field>
                <Label htmlFor="email">Email address</Label>
                <InputAny
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={registerData.email}
                  onChange={(e) =>
                    setRegisterData({...registerData, email: e.target.value})
                  }
                  placeholder="Enter your email"
                />
              </Field>

              <Field>
                <Label htmlFor="password">Password</Label>
                <InputAny
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={registerData.password}
                  onChange={(e) =>
                    setRegisterData({...registerData, password: e.target.value})
                  }
                  placeholder="Enter your password"
                />
              </Field>

              <Field>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <InputAny
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={registerData.confirmPassword}
                  onChange={(e) =>
                    setRegisterData({
                      ...registerData,
                      confirmPassword: e.target.value,
                    })
                  }
                  placeholder="Confirm your password"
                />
              </Field>
            </div>

            <div className="space-y-4">
              <ButtonAny
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700">
                {loading ? 'Creating account...' : 'Create account'}
              </ButtonAny>

              <ButtonAny
                type="button"
                variant="outline"
                onClick={() => setShowRegister(false)}
                className="w-full">
                Back to login
              </ButtonAny>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="space-y-4">
              <Field>
                <Label htmlFor="email">Email address</Label>
                <InputAny
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({...formData, email: e.target.value})
                  }
                  placeholder="Enter your email"
                />
              </Field>

              <Field>
                <Label htmlFor="password">Password</Label>
                <InputAny
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({...formData, password: e.target.value})
                  }
                  placeholder="Enter your password"
                />
              </Field>
            </div>

            <div className="space-y-4">
              <ButtonAny
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700">
                {loading ? 'Signing in...' : 'Sign in'}
              </ButtonAny>

              <ButtonAny
                type="button"
                variant="outline"
                onClick={() => setShowRegister(true)}
                className="w-full">
                Create new account
              </ButtonAny>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/login')({
  component: LoginPage,
})
